import { ContactPreferenceOptions } from '@/types/ForestOwners/ContactPreference'
import type { ForestOwnerModel } from '@/types/ForestOwners/ForestOwner'
import type {
  CreateSupplierNumberRequest,
  GetForestOwnerSupplierParams,
  SupplierModel,
  UpdateSupplierRequest,
} from '@/types/ForestOwners/Supplier'
import {
  apiGet,
  apiGetWithPagedResult,
  apiPost,
  apiPut,
  type PagedResult,
  type WithEtag,
} from '../ApiClient'

export async function getForestOwnerSupplier(
  params: GetForestOwnerSupplierParams,
): Promise<WithEtag<SupplierModel>> {
  return apiGet<SupplierModel>(
    `/forestowners/${encodeURIComponent(params.forestOwnerId)}/suppliers/${encodeURIComponent(params.supplierId)}`,
    {
      transforms: [
        [
          (root) => root.contactPerson?.contactPreference,
          (v) => ContactPreferenceOptions.from(v as string | null),
        ],
      ],
    },
  )
}

export async function updateForestOwnerSupplier(
  forestOwnerId: string,
  supplierId: string,
  body: UpdateSupplierRequest,
  etag?: string | null,
): Promise<WithEtag<SupplierModel>> {
  return apiPut<SupplierModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/suppliers/${encodeURIComponent(supplierId)}`,
    body,
    {
      etag,
      transforms: [
        [
          (root) => root.contactPerson?.contactPreference,
          (v) => ContactPreferenceOptions.from(v as string | null),
        ],
      ],
    },
  )
}

// Build an UpdateSupplierRequest that resets a supplier's exception back to the forest owner's own data
export function resetSupplierContactToForestOwner(
  supplier: SupplierModel,
  forestOwner: ForestOwnerModel,
): UpdateSupplierRequest {
  const ownerAddress = forestOwner.addresses?.primary?.address
  const ownerPhone = forestOwner.phone?.mobile ?? forestOwner.phone?.landline
  const fullName = `${forestOwner.name.firstName} ${forestOwner.name.lastName}`.trim()

  return {
    name: supplier.name,
    municipalityNumber: supplier.municipalityNumber,
    supplierNumber: supplier.supplierNumber,
    contactPerson: {
      name: {
        firstName: forestOwner.name.firstName,
        lastName: forestOwner.name.lastName,
      },
      mailAddress: {
        address: {
          streetName: ownerAddress?.streetName ?? '',
          houseNumber: ownerAddress?.houseNumber ?? '',
          postalCode: ownerAddress?.postalCode ?? '',
          city: ownerAddress?.city ?? '',
          country: ownerAddress?.country ?? 'NO',
        },
        careOf: null,
        name: fullName,
      },
      phoneNumber: ownerPhone,
      email: forestOwner.email,
      socialSecurityNumber: null,
      contactPreference: ContactPreferenceOptions.of('None'),
    },
  }
}

export async function getSuppliers(forestOwnerId: string): Promise<PagedResult<SupplierModel>> {
  const suppliers = await apiGetWithPagedResult<SupplierModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/suppliers`,
    {
      pageSize: 100,
    },
    {
      transforms: [
        [
          (root) => root.contactPerson?.contactPreference,
          (v) => ContactPreferenceOptions.from(v as string | null),
        ],
      ],
    },
  )
  return suppliers
}

// Finds the supplier a forest owner already has for a given municipality, if any.
// Used to avoid creating duplicate suppliers when one already exists for that municipality.
export function findSupplierForMunicipality(
  suppliers: SupplierModel[],
  municipalityNumber: string,
  supplierNumber?: string,
): SupplierModel | undefined {
  return suppliers.find(
    (supplier) =>
      supplier.municipalityNumber === municipalityNumber &&
      (supplierNumber === undefined || supplier.supplierNumber === supplierNumber),
  )
}

export async function createSupplierNumberForForestOwner(
  forestOwnerId: string,
  request: CreateSupplierNumberRequest,
): Promise<SupplierModel> {
  return apiPost<SupplierModel>(
    `/forestowners/${encodeURIComponent(forestOwnerId)}/suppliers`,
    request,
    {
      transforms: [
        [
          (root) => root.contactPerson?.contactPreference,
          (v) => ContactPreferenceOptions.from(v as string | null),
        ],
      ],
    },
  )
}
